import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { BuildingNameField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for BuildingName
const FIELD_MAPPING: Record<string, string> = {
  [BuildingNameField.KEY]: 'BLDG_NAME_KEY',
  [BuildingNameField.NAME]: 'BLDG_NAME_NAME',
  [BuildingNameField.RELATED_KEY]: 'BLDG_NAME_RELATED_BLDG_NAME_KEY',
  [BuildingNameField.STATUS]: 'BLDG_NAME_STATUS',
  [BuildingNameField.URL]: 'BLDG_NAME_URL',
  [BuildingNameField.COUNTY]: 'BLDG_NAME_COUNTY',
  [BuildingNameField.STATE]: 'BLDG_NAME_STATE',
  [BuildingNameField.MODIFICATION_TIMESTAMP]:
    'BLDG_NAME_MODIFICATION_TIMESTAMP',
};

export class BuildingNameResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[BuildingNameField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // Buildings don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // Buildings are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // Buildings are not directly related to listings
  }

  // Utility methods for building resources
  getName(): string | undefined {
    return this[FIELD_MAPPING[BuildingNameField.NAME]];
  }

  getStatus(): string | undefined {
    return this[FIELD_MAPPING[BuildingNameField.STATUS]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[BuildingNameField.COUNTY]];
  }

  getState(): string | undefined {
    return this[FIELD_MAPPING[BuildingNameField.STATE]];
  }
}

export class BuildingNameResourceManager extends BaseResourceManager<BuildingNameResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(
      ResoResource.BUILDING_NAME,
      executeQuery,
      fieldTranslator,
      taskManager,
      {
        ...options,
        useTranslation: false, // Disable translation
      },
    );
  }

  /**
   * Map field name using our custom mapping
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to BuildingNameResource
   */
  protected mapFromApiCore(apiData: any): BuildingNameResource {
    return new BuildingNameResource(apiData);
  }

  /**
   * Core mapping function from BuildingNameResource to API data
   */
  protected mapToApiCore(resource: BuildingNameResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find buildings by name
   */
  public async findByName(name: string): Promise<BuildingNameResource[]> {
    return this.findListByField(BuildingNameField.NAME, name);
  }

  /**
   * Find buildings by county
   */
  public async findByCounty(county: string): Promise<BuildingNameResource[]> {
    return this.findListByField(BuildingNameField.COUNTY, county);
  }

  /**
   * Find buildings by state
   */
  public async findByState(state: string): Promise<BuildingNameResource[]> {
    return this.findListByField(BuildingNameField.STATE, state);
  }

  /**
   * Find buildings by state and county
   */
  public async findByStateAndCounty(
    state: string,
    county: string,
  ): Promise<BuildingNameResource[]> {
    return this.findListByFields({
      [BuildingNameField.STATE]: state,
      [BuildingNameField.COUNTY]: county,
    });
  }

  /**
   * Override findListByField to use our field mapping
   */
  public async findListByField(
    field: string,
    value: any,
  ): Promise<BuildingNameResource[]> {
    const mappedField = this.mapFieldName(field);
    return super.findListByField(mappedField, value);
  }

  /**
   * Override findByField to use our field mapping
   */
  public async findByField(
    field: string,
    value: any,
  ): Promise<BuildingNameResource | null> {
    const mappedField = this.mapFieldName(field);
    return super.findByField(mappedField, value);
  }
}

/**
 * Create a new BuildingNameResource instance with required fields
 */
export function createBuildingNameResource(
  data: Partial<Record<string, any>> = {},
): BuildingNameResource {
  const defaults = {};

  return new BuildingNameResource({
    ...defaults,
    ...data,
  });
}
